import { Box, Button, Menu, Stack, Tooltip, Typography } from "@mui/material";
import { ContentCopy, MobiledataOffOutlined } from "@mui/icons-material";
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
  GridToolbarQuickFilter,
  GridToolbarProps,
} from "@mui/x-data-grid-premium";
import { useState, ChangeEvent, MouseEvent } from "react";
import { StyledRadioButtonsGroup } from "../StyledRadioButtons/StyledRadioButtonsGroup";
import { StyledRadioButton } from "../StyledRadioButtons/StyledRadioButton";
import { percentileGradient } from "../PercentileCell";

declare module "@mui/x-data-grid-premium" {
  interface ToolbarPropsOverrides {
    percentileProps: PercentileProps;
    isMobile: boolean;
  }
}

interface PercentileProps {
  rankMode: "off" | "ranks" | "percentiles";
  setRankMode: (mode: "off" | "ranks" | "percentiles") => void;
}
interface CustomDataGridToolbarProps extends GridToolbarProps {
  percentileProps: PercentileProps;
  isMobile: boolean;
}

export default function CustomDataGridToolbar(
  props: CustomDataGridToolbarProps,
) {
  const { csvOptions, excelOptions, printOptions, percentileProps, isMobile } =
    props;
  const { rankMode, setRankMode } = percentileProps;
  const [anchorElMenu, setAnchorElMenu] = useState<null | HTMLButtonElement>(
    null,
  );
  const openMenu = Boolean(anchorElMenu);
  const [copied, setCopied] = useState(false);
  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <GridToolbarContainer
      sx={
        isMobile
          ? {
              ".MuiButton-startIcon": { mr: 0 },
              ".MuiButton-root": { minWidth: "auto", px: 0.75 },
            }
          : undefined
      }
    >
      <Tooltip title="Show/hide, reorder, rename, and add custom columns">
        <span>
          <GridToolbarColumnsButton
            slotProps={{
              button: { children: isMobile ? undefined : "Columns" },
            }}
          />
        </span>
      </Tooltip>
      <GridToolbarFilterButton />
      <Button
        onClick={(event: MouseEvent<HTMLButtonElement>) =>
          setAnchorElMenu(event.currentTarget)
        }
        startIcon={<MobiledataOffOutlined />}
        sx={isMobile ? { minWidth: "auto", px: 0.75 } : {}}
      >
        {isMobile ? "" : rankMode === "ranks" ? "Ranks" : "Percentiles"}
      </Button>
      <Menu
        id="percentile-menu"
        anchorEl={anchorElMenu}
        open={openMenu}
        onClose={() => setAnchorElMenu(null)}
      >
        <Box px={2}>
          <StyledRadioButtonsGroup
            id="percentile"
            fieldLabel="Mode"
            value={rankMode}
            row={true}
            textSize="sm"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setRankMode(e.target.value as "off" | "ranks" | "percentiles")
            }
          >
            <StyledRadioButton
              value="off"
              label="Off"
              textSize="sm"
              selected={rankMode === "off"}
            />
            <StyledRadioButton
              value="ranks"
              label="Ranks"
              textSize="sm"
              selected={rankMode === "ranks"}
            />

            <StyledRadioButton
              value="percentiles"
              label="Percentiles"
              textSize="sm"
              selected={rankMode === "percentiles"}
            />
          </StyledRadioButtonsGroup>
        </Box>
      </Menu>
      {!isMobile && (
        <GridToolbarExport
          csvOptions={csvOptions}
          excelOptions={excelOptions}
          printOptions={printOptions}
        />
      )}
      <Tooltip title={copied ? "Copied!" : "Copy link to this view"}>
        <Button
          onClick={handleCopyLink}
          startIcon={<ContentCopy />}
          sx={isMobile ? { minWidth: "auto", px: 0.75 } : {}}
        >
          {isMobile ? "" : copied ? "Copied!" : "Share"}
        </Button>
      </Tooltip>
      <Box sx={{ flexGrow: 1 }} />
      {!isMobile && rankMode !== "off" && (
        <Stack mr={4} alignItems="center">
          <Typography variant="caption" color="textSecondary">
            {rankMode === "percentiles" ? "Percentile" : "Rank"}
          </Typography>
          <div
            className="grad-chart"
            style={{
              background: percentileGradient(
                rankMode,
                // Rank legends are labelled 1st -> Last, so the high end sits left.
                rankMode === "ranks" ? "to left" : "to right",
              ),
            }}
          />
          <Box display="flex" justifyContent="space-between" width="100%">
            <Typography variant="caption" color="textSecondary">
              {rankMode === "percentiles" ? "0" : "1st"}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {rankMode === "percentiles" ? "100" : "Last"}
            </Typography>
          </Box>
        </Stack>
      )}
      <GridToolbarQuickFilter sx={isMobile ? { maxWidth: "140px" } : {}} />
    </GridToolbarContainer>
  );
}
